import { FEATURE_FLAGS } from "@solomon-apps/config";
import { type ClientOperatingContextSummary, useOrgAuthStore } from "@solomon-apps/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { listClientOperatingContexts } from "@/services/clientOperatingContexts";
import { isClientConsoleAvailable } from "@/utils/clientConsoleAccess";

export const CLIENT_OPERATING_CONTEXTS_QUERY_KEY = "client-operating-contexts";

export function selectedContextHasPlatformEligibility(
    contexts: readonly ClientOperatingContextSummary[],
    selectedClientContextUuid: string | null
): boolean {
    return Boolean(
        selectedClientContextUuid &&
            contexts.some(
                (context) =>
                    context.issuerClientUuid === selectedClientContextUuid &&
                    context.platformEligibility === "eligible" &&
                    // Paused or revoked by the Distributor (absent: an
                    // older yaas-api, where it cannot be either).
                    (context.platformAccessState ?? "Active") === "Active" &&
                    context.status === "Active" &&
                    context.issuerCapabilityStatus === "Active" &&
                    !context.readOnly
            )
    );
}

/**
 * Whether the selected relationship has the Distributions access level live,
 * the Distributor section's gate. yaas-api decides it (requested at creation
 * AND Platform Access live). A yaas-api that predates the field omits it; the
 * section then stays as it was before the gate, shown.
 */
export function selectedContextHasDistributionsAccess(
    contexts: readonly ClientOperatingContextSummary[],
    selectedClientContextUuid: string | null
): boolean {
    const context = selectedClientContextUuid
        ? contexts.find((candidate) => candidate.issuerClientUuid === selectedClientContextUuid)
        : undefined;
    return Boolean(context && context.distributionsAccess !== false);
}

/**
 * Whether the selected relationship has the API Access level live, the API
 * keys' gate. yaas-api decides it (requested at creation, not paused or
 * revoked, AND Platform Access live) and refuses the key routes without it.
 * A yaas-api that predates the field omits it; API keys then stay as they
 * were before the gate, shown.
 */
export function selectedContextHasApiAccess(
    contexts: readonly ClientOperatingContextSummary[],
    selectedClientContextUuid: string | null
): boolean {
    const context = selectedClientContextUuid
        ? contexts.find((candidate) => candidate.issuerClientUuid === selectedClientContextUuid)
        : undefined;
    return Boolean(context && context.apiAccess !== false);
}

/** One authenticated context discovery result shared by the gate, shell, and
 * completion flows. A persisted store value is only presentation state; this
 * query is the authorization source. */
export function useClientPlatformAccess({ refreshOnMount = false }: { refreshOnMount?: boolean } = {}): {
    consoleAvailable: boolean;
    isPending: boolean;
    isSettled: boolean;
    isSelfClient: boolean;
    platformEligible: boolean;
    distributionsAccess: boolean;
    apiAccess: boolean;
} {
    const tokens = useOrgAuthStore((state) => state.tokens);
    const me = useOrgAuthStore((state) => state.me);
    const selectedClientContextUuid = useOrgAuthStore((state) => state.selectedClientContextUuid);
    const setClientOperatingContexts = useOrgAuthStore((state) => state.setClientOperatingContexts);
    const authReady = Boolean(tokens && tokens.expiresAt >= Date.now() + 30_000);
    const authIdentity = me ? `${me.org.uuid}:${me.user.uuid}` : null;
    const query = useQuery({
        queryKey: [CLIENT_OPERATING_CONTEXTS_QUERY_KEY, authIdentity],
        queryFn: listClientOperatingContexts,
        enabled: authReady && Boolean(authIdentity),
        staleTime: 0,
        // The entry gate owns mount refreshes. A gated page must reuse the
        // result: fetching on its mount closes the gate, unmounts the page,
        // and repeats the request as soon as the gate opens again.
        refetchOnMount: refreshOnMount ? "always" : false,
        // Not on window focus either: a wallet popup (Phantom's sign request)
        // takes focus and returns it, and the refetch closed the gate,
        // unmounting a page mid-verification and dropping the signature.
        // Entry and explicit invalidation still refresh access.
        refetchOnWindowFocus: false
    });

    useEffect(() => {
        if (query.data) {
            setClientOperatingContexts(query.data.contexts, query.data.defaultContextUuid);
        }
    }, [query.data, setClientOperatingContexts]);

    const isPending = authReady && Boolean(authIdentity) && query.isFetching;
    const isSettled = authReady && Boolean(authIdentity) && !query.isFetching && !query.isError && Boolean(query.data);
    const isSelfClient =
        isSettled &&
        Boolean(
            query.data?.contexts.some(
                (context) => context.issuerClientUuid === selectedClientContextUuid && context.isSelfClient
            )
        );
    const platformEligible =
        isSettled && selectedContextHasPlatformEligibility(query.data?.contexts ?? [], selectedClientContextUuid);
    const distributionsAccess =
        isSettled && selectedContextHasDistributionsAccess(query.data?.contexts ?? [], selectedClientContextUuid);
    const apiAccess = isSettled && selectedContextHasApiAccess(query.data?.contexts ?? [], selectedClientContextUuid);
    return {
        isPending,
        isSettled,
        isSelfClient,
        platformEligible,
        distributionsAccess,
        apiAccess,
        consoleAvailable: isClientConsoleAvailable({
            consoleAccessEnabled: FEATURE_FLAGS.clientConsoleAccess,
            isSelfClient,
            platformEligible
        })
    };
}
